/**
 * Role hierarchy shared by server and web UI.
 *
 * Roles are ordered from most to least privileged. A user with a higher role
 * has every permission of the lower roles (`admin` > `editor` > `reader`).
 */
export const ROLES = ["admin", "editor", "reader"] as const;

export type Role = (typeof ROLES)[number];

export const DEFAULT_ROLE: Role = "reader";

const RANK: Record<Role, number> = {
  admin: 3,
  editor: 2,
  reader: 1,
};

export function isRole(value: unknown): value is Role {
  return typeof value === "string" && (ROLES as readonly string[]).includes(value);
}

/**
 * Returns true when `user` holds `minRole` or a higher role.
 * Unknown or missing roles never satisfy any requirement.
 */
export function hasRole(user: { role?: string | null } | null | undefined, minRole: Role): boolean {
  const role = user?.role;
  if (!isRole(role)) return false;
  return RANK[role] >= RANK[minRole];
}
